import { Suspense, lazy, useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/useAuthStore';
import { api } from './lib/api-client';
import { registerServiceWorker } from './lib/push';
import ScrollToTop from './components/ui/ScrollToTop';
import { FullPageState } from './components/ui/PageState';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Start from './pages/Start';
import Onboarding from './pages/Onboarding';
import Home from './pages/Home';
import Chat from './pages/Chat';
import ChatHistory from './pages/ChatHistory';
import AddBaby from './pages/AddBaby';
import Profile from './pages/Profile';
import AuthCallback from './pages/AuthCallback';

// Route-level code splitting for the pages that carry their own weight and
// are rarely part of the same visit as the core loop (/today → /chat):
// Nutrition bundles ~130 KB of recipe and section data, Privacy/Imprint are
// long legal text, Admin is founder-only. Keeps the first paint of the
// authenticated app from paying for them on every load.
const Nutrition = lazy(() => import('./pages/Nutrition'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Imprint = lazy(() => import('./pages/Imprint'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Admin = lazy(() => import('./pages/Admin'));

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthStore();
  const location = useLocation();
  if (loading) {
    return <FullPageState />;
  }
  if (!user) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }
  // Note: anonymous users (user.is_anonymous === true) pass this guard
  // intentionally. They have a real auth.users row and a JWT — they're
  // authenticated, just not yet identified by email.
  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthStore();
  if (loading) return null;
  if (user) return <Navigate to="/today" replace />;
  return <>{children}</>;
}

export default function App() {
  const initialize = useAuthStore((s) => s.initialize);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Country auto-detection (Phase 5 step 1). Fires once per user per app
  // session when an identified user becomes available. Anonymous users
  // skip detection — they convert later and detection fires then.
  // Silent: no UI feedback, errors swallowed; local dev returns
  // 'unavailable' and is a no-op.
  const detectedForUserRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user || user.is_anonymous) return;
    if (detectedForUserRef.current === user.id) return;
    detectedForUserRef.current = user.id;
    api.profile.detectCountry().catch(() => {
      /* silent — failure is non-blocking; user can set country in Profile */
    });
  }, [user]);

  // Service Worker registration (Phase 5 step 2). Production only — Vite
  // dev hot-reload interacts badly with cached service workers. Fires
  // once per app mount regardless of auth state. Subscription to push
  // (which requires the registration) lives in Step 3 and is gated on
  // an explicit Notification.permission grant.
  const swRegisteredRef = useRef(false);
  useEffect(() => {
    if (!import.meta.env.PROD) return;
    if (swRegisteredRef.current) return;
    swRegisteredRef.current = true;
    registerServiceWorker().catch(() => {
      /* silent — SW failure doesn't break the app, just disables push */
    });
  }, []);

  return (
    <Suspense fallback={<FullPageState />}>
      <ScrollToTop />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/welcome" element={<Navigate to="/" replace />} />

      {/* /start — anonymous onboarding (no signup required).
          Public route: any visitor can land here. The page itself
          handles the anon sign-in + baby creation flow. */}
      <Route
        path="/start"
        element={
          <RedirectIfAuthed>
            <Start />
          </RedirectIfAuthed>
        }
      />

      <Route
        path="/login"
        element={
          <RedirectIfAuthed>
            <Login />
          </RedirectIfAuthed>
        }
      />
      <Route
        path="/signup"
        element={
          <RedirectIfAuthed>
            <Signup />
          </RedirectIfAuthed>
        }
      />
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <Onboarding />
          </RequireAuth>
        }
      />
      <Route
        path="/today"
        element={
          <RequireAuth>
            <Home />
          </RequireAuth>
        }
      />

      {/* Chat routes — order matters. /chat/history is a literal path and
          must come BEFORE the /chat/:sessionId pattern so it doesn't get
          matched as a session ID. */}
      <Route
        path="/chat"
        element={
          <RequireAuth>
            <Chat />
          </RequireAuth>
        }
      />
      <Route
        path="/chat/history"
        element={
          <RequireAuth>
            <ChatHistory />
          </RequireAuth>
        }
      />
      <Route
        path="/chat/:sessionId"
        element={
          <RequireAuth>
            <Chat />
          </RequireAuth>
        }
      />

      {/* /settings is preserved as a redirect to /profile. The Settings
          page was absorbed into Profile on 2026-05-31; old bookmarks and
          AddBaby's back-links continue to work via this redirect. */}
      <Route path="/settings" element={<Navigate to="/profile" replace />} />
      <Route
        path="/babies/new"
        element={
          <RequireAuth>
            <AddBaby />
          </RequireAuth>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuth>
            <Profile />
          </RequireAuth>
        }
      />
      <Route
        path="/nutrition"
        element={
          <RequireAuth>
            <Nutrition />
          </RequireAuth>
        }
      />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <Admin />
          </RequireAuth>
        }
      />

      {/* Public — reachable for both authed and unauthed visitors.
          Pricing handles its own auth state internally. Imprint and
          Privacy must be public per Swiss UWG / DSGVO. */}
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/imprint" element={<Imprint />} />
      <Route path="/privacy" element={<Privacy />} />

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
